import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  CreateQuestionTemplateRequest,
  QuestionTemplate,
  UpdateQuestionTemplateRequest,
} from '../../types/case-container'
import {
  CategorySelector,
  ErrorBanner,
  Field,
  FormFooter,
  ModalHeader,
  TextArea,
  TextInput,
} from './template-form-modal/template-form-modal.parts'

export interface TemplateFormModalProps {
  title: string
  categories: string[]
  template?: QuestionTemplate
  onClose: () => void
  onSubmit: (
    data: CreateQuestionTemplateRequest | UpdateQuestionTemplateRequest
  ) => void | Promise<void>
  submitting: boolean
  error: string | null
}

const TEXTAREA_ROWS = 4

export function TemplateFormModal({
  title,
  categories,
  template,
  onClose,
  onSubmit,
  submitting,
  error,
}: TemplateFormModalProps): ReactElement {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')

  const [description, setDescription] = useState(template?.description ?? '')
  const [questionText, setQuestionText] = useState(template?.questionText ?? '')
  const [category, setCategory] = useState(template?.category ?? '')
  const [newCategory, setNewCategory] = useState('')

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!description || !questionText) return
    const resolvedCategory = newCategory || category
    void onSubmit({
      questionText,
      ...(description ? { description } : {}),
      ...(resolvedCategory ? { category: resolvedCategory } : {}),
    } satisfies CreateQuestionTemplateRequest)
  }

  const canSubmit = Boolean(description) && Boolean(questionText)
  const saveLabel = template ? tCommon('actions.save') : t('templates.createTemplate')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <ModalHeader title={title} onClose={onClose} />
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <ErrorBanner message={error} />}
          <Field label={t('templates.templateName')} required>
            <TextInput
              value={description}
              onChange={setDescription}
              placeholder={t('templates.namePlaceholder')}
              required
            />
          </Field>
          <Field label={t('templates.questionText')} required>
            <TextArea
              value={questionText}
              onChange={setQuestionText}
              placeholder={t('templates.questionPlaceholder')}
              rows={TEXTAREA_ROWS}
              required
            />
          </Field>
          <CategorySelector
            category={category}
            onCategoryChange={setCategory}
            newCategory={newCategory}
            onNewCategoryChange={setNewCategory}
            categories={categories}
            t={t}
          />
          <FormFooter
            onClose={onClose}
            submitting={submitting}
            canSubmit={canSubmit}
            saveLabel={saveLabel}
            cancelLabel={tCommon('actions.cancel')}
          />
        </form>
      </div>
    </div>
  )
}
